import { Controller } from "@hotwired/stimulus"

// P1-9 (hardened): driver for the disconnect-Stripe confirm flow.
//
// Three states the dialog can land in after the impact preview fetch:
//   - blockers   → preview.can_disconnect is false. Lists each blocker.
//                  The destructive button is not rendered in this branch.
//   - proceed    → preview.can_disconnect is true. Lists the side effects
//                  (cards unlinked, series ended, payment-mode warning),
//                  and gates the destructive submit behind a type-to-confirm
//                  input (the practitioner must type the practice slug).
//   - error      → preview API itself failed. Practitioner can retry by
//                  closing + re-opening the dialog.
//
// The actual disconnect mutation is a plain form post handled server-side
// (Practitioners::StripeController#disconnect) with the typed slug as
// confirm_token; the controller server-side re-validates the token.
export default class extends Controller {
  static targets = [
    "dialog",
    "loading",
    "blockers",
    "blockersList",
    "proceed",
    "sideEffects",
    "confirmInput",
    "confirmButton",
    "form",
    "tokenField",
    "error",
    "errorMessage"
  ]

  static values = {
    previewUrl: String,
    actionUrl: String,
    slug: String
  }

  async open(event) {
    event?.preventDefault()
    this.showDialog()
    this.showOnly("loading")

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.previewUrlValue, {
        method: "GET",
        headers: { Accept: "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin"
      })

      if (!resp.ok) {
        this.renderError("Couldn't check impact. Try again.")
        return
      }

      const data = await resp.json()
      this.renderPreview(data)
    } catch (e) {
      this.renderError("Couldn't reach the server. Check your connection and try again.")
    }
  }

  close(event) {
    event?.preventDefault()
    this.hideDialog()
    // Reset typed input so a re-open starts clean.
    if (this.hasConfirmInputTarget) this.confirmInputTarget.value = ""
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true
    if (this.hasTokenFieldTarget) this.tokenFieldTarget.value = ""
  }

  onConfirmInput() {
    if (!this.hasConfirmInputTarget || !this.hasConfirmButtonTarget) return
    const typed = this.confirmInputTarget.value.trim().toLowerCase()
    const expected = this.slugValue.trim().toLowerCase()
    const matches = typed.length > 0 && typed === expected
    this.confirmButtonTarget.disabled = !matches
    if (this.hasTokenFieldTarget) {
      this.tokenFieldTarget.value = matches ? this.confirmInputTarget.value : ""
    }
  }

  // ── rendering ──────────────────────────────────────────────────────────

  renderPreview(data) {
    if (data.connected === false) {
      this.renderError("Stripe is not currently connected.")
      return
    }

    if (!data.can_disconnect) {
      this.renderBlockers(data.blockers || [])
      return
    }

    this.renderProceed(data)
  }

  renderBlockers(blockers) {
    if (!this.hasBlockersListTarget) return
    this.blockersListTarget.innerHTML = ""
    blockers.forEach((b) => {
      const li = document.createElement("li")
      li.className = "flex items-start gap-2"
      li.innerHTML = `<span class="text-danger-700 mt-0.5">•</span><span>${escapeHtml(b.message)}</span>`
      this.blockersListTarget.appendChild(li)
    })
    this.showOnly("blockers")
  }

  renderProceed(data) {
    if (this.hasSideEffectsTarget) {
      this.sideEffectsTarget.innerHTML = ""
      const effects = data.side_effects || []
      if (effects.length === 0) {
        const note = document.createElement("p")
        note.className = "text-sm text-neutral-600"
        note.textContent = "No saved cards, no active series, no outstanding invoices. Disconnect is a clean no-op."
        this.sideEffectsTarget.appendChild(note)
      } else {
        effects.forEach((effect) => {
          const box = document.createElement("div")
          const palette = effect.kind === "payment_mode_will_block_new_bookings"
            ? "bg-warning-50 border-warning-200 text-warning-800"
            : "bg-info-50 border-info-200 text-info-800"
          box.className = `rounded-lg border p-3 ${palette}`
          const p = document.createElement("p")
          p.className = "text-sm"
          p.textContent = effect.message
          box.appendChild(p)
          this.sideEffectsTarget.appendChild(box)
        })
      }
    }

    if (this.hasFormTarget) {
      this.formTarget.action = this.actionUrlValue
    }
    if (this.hasConfirmInputTarget) {
      this.confirmInputTarget.value = ""
      this.confirmInputTarget.focus()
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true
    }
    this.showOnly("proceed")
  }

  renderError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
    this.showOnly("error")
  }

  // ── dialog visibility helpers ──────────────────────────────────────────

  showDialog() {
    if (!this.hasDialogTarget) return
    this.dialogTarget.classList.remove("hidden")
    this.dialogTarget.classList.add("flex")
    document.body.style.overflow = "hidden"
  }

  hideDialog() {
    if (!this.hasDialogTarget) return
    this.dialogTarget.classList.add("hidden")
    this.dialogTarget.classList.remove("flex")
    document.body.style.overflow = ""
  }

  showOnly(name) {
    const sections = ["loading", "blockers", "proceed", "error"]
    sections.forEach((section) => {
      const target = `has${section.charAt(0).toUpperCase() + section.slice(1)}Target`
      if (this[target]) {
        const el = this[`${section}Target`]
        el.classList.toggle("hidden", section !== name)
      }
    })
  }
}

function escapeHtml(s) {
  const div = document.createElement("div")
  div.textContent = s
  return div.innerHTML
}
